import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const kind = params.get("type");
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [plan, setPlan] = useState("");
  const [subStatus, setSubStatus] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(kind === "assessment" ? `/public/assessment/status/${sessionId}` : `/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPlan(data.plan);
          setSubStatus(data.subscription_status || "");
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      tries.current += 1;
      if (tries.current >= MAX_TRIES) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, kind, refreshUser]);

  return (
    <div className="min-h-screen bg-[#F4F1EA] flex flex-col items-center justify-center p-6 font-body gap-6">
      <div className="w-full max-w-md border border-zinc-200 bg-white p-10 text-center" data-testid="payment-success-card">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-zinc-400 animate-spin" />
            <h1 className="font-display text-3xl font-light mt-6">Confirming your payment…</h1>
            <p className="text-zinc-500 text-sm mt-2">This takes just a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-[#156F52]" />
            <h1 className="font-display text-3xl font-light mt-6" data-testid="payment-success-title">
              {kind === "assessment" ? "Payment received" : "You're subscribed"}
            </h1>
            <p className="text-zinc-500 text-sm mt-2">
              {kind === "assessment"
                ? "Thank you for your Baseline Marketing Assessment. Our team will email you shortly to request the information needed to begin. A receipt has been sent to your email."
                : <>Your firm is now on the <span className="font-medium capitalize">{plan}</span> plan. All included features are unlocked.</>}
            </p>
            {kind === "assessment" ? (
              <Button asChild className="mt-8 bg-[#0B1F33] text-white rounded-none h-11 px-6 group hover:bg-zinc-800" data-testid="to-home-button">
                <Link to="/">Return to homepage <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
              </Button>
            ) : (
              <Button asChild className="mt-8 bg-[#0B1F33] text-white rounded-none h-11 px-6 group hover:bg-zinc-800" data-testid="to-dashboard-button">
                <Link to="/app">Go to dashboard <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
              </Button>
            )}
            <div className="mt-8 pt-5 border-t border-zinc-200" data-testid="receipt-attribution">
              <p className="font-mono-data text-[11px] uppercase tracking-widest text-zinc-500">RetainProof™ {kind === "assessment" ? "Assessment" : "Subscription"} Receipt</p>
              <p className="text-[11px] text-zinc-400 mt-1">Provided by CMD Marketing Enterprises LLC through CMD+DesignLab</p>
            </div>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="h-14 w-14 mx-auto text-[#B54747]" />
            <h1 className="font-display text-3xl font-light mt-6">Payment not confirmed</h1>
            <p className="text-zinc-500 text-sm mt-2">
              {status === "timeout"
                ? "We couldn't confirm the payment yet. If you completed checkout, it may take a minute to reflect."
                : "Something went wrong with this session."}
            </p>
            <Button asChild variant="outline" className="mt-8 rounded-none h-11 px-6 border-zinc-300" data-testid="back-to-billing-button">
              <Link to="/app/billing">Back to billing</Link>
            </Button>
          </>
        )}
      </div>
      {status === "paid" && kind !== "assessment" && (
        <div className="w-full max-w-md text-left" data-testid="payment-onboarding">
          <OnboardingChecklist variant="panel" />
        </div>
      )}
    </div>
  );
}
